import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useInviteRotation, useUpdateInviteRotation } from "@/hooks/sms/useInviteRotation";

/**
 * The Order Invite on/off card. Enabling for the first time is gated by an
 * AlertDialog (it starts a daily, ongoing send); disabling is one tap. Copy on
 * disable makes clear today's already-queued invites still go out — the same
 * contract as the timing card's "changes apply from the next batch".
 */
export function InviteOnOffCard() {
  const { data: rotation } = useInviteRotation();
  const updateRotation = useUpdateInviteRotation();
  const [enabled, setLocalEnabled] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (rotation) setLocalEnabled(rotation.enabled);
  }, [rotation]);

  if (!rotation) return null;

  const setEnabled = (on: boolean) => {
    const previous = enabled;
    setLocalEnabled(on);
    updateRotation.mutate(
      { store_id: rotation.store_id, enabled: on },
      { onError: () => setLocalEnabled(previous) },
    );
  };

  return (
    <Card>
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Label id="invite-onoff-label" htmlFor="invite-onoff" className="text-sm font-semibold text-foreground">
              Order Invite
            </Label>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {enabled
                ? "On — recent customers get one invite a day to join your text club."
                : "Off — no invites are sent."}
            </p>
          </div>
          <Switch
            id="invite-onoff"
            aria-labelledby="invite-onoff-label"
            checked={enabled}
            onCheckedChange={(checked) => {
              if (checked) setDialogOpen(true);
              else setEnabled(false);
            }}
          />
        </div>
      </CardContent>

      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Turn on Order Invite?</AlertDialogTitle>
            <AlertDialogDescription>
              This starts texting eligible past-ordering customers daily, going forward, until you turn it back off.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not yet</AlertDialogCancel>
            <AlertDialogAction onClick={() => setEnabled(true)}>Turn on</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
